import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EntityService from '../../services/EntityService';
import { API } from '../../shared/api/Api';

/**
 * Fetch all categories
 */
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async ({ token, params }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getAllEnties(
        token,
        API.Categories.app_categories_all,
        params
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          items: response.data.data.items,
          currentPage: response.data.data.current_page,
          totalPages: response.data.data.last_page,
          total: response.data.data.total,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch categories');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch categories');
    }
  }
);

/**
 * Fetch a single category by ID
 */
export const fetchCategoryById = createAsyncThunk(
  'categories/fetchCategoryById',
  async ({ token, categoryId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getEntity(
        token,
        API.Categories.app_categories_get,
        categoryId
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to fetch category');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch category');
    }
  }
);

/**
 * Create a new category
 */
export const createCategory = createAsyncThunk(
  'categories/createCategory',
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });

      const response = await EntityService.addEntity(
        token,
        API.Categories.app_categories_add,
        data
      );
      if (response.data.status === 200 || response.data.status === 201) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create category');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create category';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Update an existing category
 */
export const updateCategory = createAsyncThunk(
  'categories/updateCategory',
  async ({ token, categoryId, formData, initialData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Only append changed fields
      Object.keys(formData).forEach((key) => {
        const currentValue = formData[key];
        const initialValue = initialData[key];

        // Handle file inputs differently
        if (currentValue instanceof File) {
          data.append(key, currentValue);
        }
        // Handle other changed fields
        else if (currentValue !== initialValue) {
          data.append(key, currentValue);
        }
      });

      const response = await EntityService.updateEntity(
        token,
        API.Categories.app_categories_update,
        categoryId,
        data
      );
      if (response.data.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to update category');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update category';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Delete a category
 */
export const deleteCategory = createAsyncThunk(
  'categories/deleteCategory',
  async ({ token, categoryId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.deleteEntity(
        token,
        API.Categories.app_categories_delete,
        categoryId
      );
      if (response.data.status === 200) {
        return categoryId;
      }
      return rejectWithValue(response.data.message || 'Failed to delete category');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete category');
    }
  }
);

const initialState = {
  // List state
  entities: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
  },
  
  // Selected entity state
  selectedEntity: null,
  initialFormData: null,
  
  // Status states
  listStatus: 'idle',
  detailStatus: 'idle',
  createStatus: 'idle',
  updateStatus: 'idle',
  deleteStatus: 'idle',
  
  // Error states
  listError: null,
  detailError: null,
  createError: null,
  updateError: null,
  deleteError: null,
  
  // Form validation errors
  formErrors: {},
};

const categorySlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    clearSelectedEntity: (state) => {
      state.selectedEntity = null;
      state.initialFormData = null;
      state.detailStatus = 'idle';
      state.detailError = null;
      state.formErrors = {};
    },
    
    clearFormErrors: (state) => {
      state.formErrors = {};
      state.createError = null;
      state.updateError = null;
    },
    
    resetSubmissionStatus: (state) => {
      state.createStatus = 'idle';
      state.updateStatus = 'idle';
      state.createError = null;
      state.updateError = null;
      state.formErrors = {};
    },
    
    updateFormData: (state, action) => {
      if (state.selectedEntity) {
        state.selectedEntity = {
          ...state.selectedEntity,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch categories list
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.entities = action.payload.items;
        state.pagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
        state.listError = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload;
      });

    // Fetch category by ID
    builder
      .addCase(fetchCategoryById.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchCategoryById.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.detailError = null;
        state.formErrors = {};
      })
      .addCase(fetchCategoryById.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.detailError = action.payload;
      });

    // Create category
    builder
      .addCase(createCategory.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createCategory.fulfilled, (state) => {
        state.createStatus = 'succeeded';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.createStatus = 'failed';
        const errorData = action.payload;
        state.createError = errorData.message || 'Failed to create category';
        state.formErrors = errorData.errors || {};
      });

    // Update category
    builder
      .addCase(updateCategory.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.updateStatus = 'failed';
        const errorData = action.payload;
        state.updateError = errorData.message || 'Failed to update category';
        state.formErrors = errorData.errors || {};
      });

    // Delete category
    builder
      .addCase(deleteCategory.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.entities = state.entities.filter(cat => cat.id !== action.payload);
        state.deleteError = null;
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export const {
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
} = categorySlice.actions;

// Selectors
export const selectCategories = (state) => state.categories.entities;
export const selectCategoriesPagination = (state) => state.categories.pagination;
export const selectSelectedCategory = (state) => state.categories.selectedEntity;
export const selectInitialFormData = (state) => state.categories.initialFormData;
export const selectCategoriesListStatus = (state) => state.categories.listStatus;
export const selectCategoryDetailStatus = (state) => state.categories.detailStatus;
export const selectCategoryCreateStatus = (state) => state.categories.createStatus;
export const selectCategoryUpdateStatus = (state) => state.categories.updateStatus;
export const selectCategoryDeleteStatus = (state) => state.categories.deleteStatus;
export const selectCategoriesListError = (state) => state.categories.listError;
export const selectCategoryDetailError = (state) => state.categories.detailError;
export const selectCategoryCreateError = (state) => state.categories.createError;
export const selectCategoryUpdateError = (state) => state.categories.updateError;
export const selectCategoryDeleteError = (state) => state.categories.deleteError;
export const selectCategoryFormErrors = (state) => state.categories.formErrors;

export default categorySlice.reducer;
